import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Divider, Alert, CircularProgress, useTheme, alpha,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  WineBar as WineListIcon,
  LocalBar as CocktailIcon,
  RestaurantMenu as FoodIcon,
  Layers as MixedIcon,
  MoreHoriz as OtherIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuService from '../../../../services/menuService';
import type { MenuType, MenuStatus } from '../../../../types/menu';
import { menuTypeLabel } from './menuTypeLabels';

/**
 * Phase 4.K — use-case-first menu creation.
 *
 * Create mode no longer exposes the raw `MenuType` enum as a single dropdown.
 * The operator first picks a *use case* card (Wine List, Cocktail Menu, Food
 * Menu, Mixed Menu, Other). The use case maps to a concrete `MenuType`:
 *
 *   WINE_LIST → defaults to FULL; reveals a subtype select with the 5 wine-list
 *               variants (FULL / BY_THE_GLASS / RESERVE / EVENT / PRIVATE)
 *   COCKTAIL  → fixed to MenuType 'COCKTAIL'
 *   FOOD      → fixed to MenuType 'FOOD' + non-blocking advisory that
 *               food-item authoring is not available yet
 *   MIXED     → fixed to MenuType 'MIXED'
 *   OTHER     → reveals the full `MenuType` dropdown with friendly labels for
 *               edge cases or future types (BEER, SPIRITS, TASTING, RETAIL,
 *               OTHER)
 *
 * Edit mode keeps the simple dropdown (no card picker) so operators editing an
 * existing menu can change its type without re-doing the use-case selection.
 *
 * The submitted payload is unchanged — backend still receives a single
 * `MenuType` enum value. No schema, controller, or route changes.
 */

// ── Use cases ────────────────────────────────────────────────────────────────

type UseCase = 'WINE_LIST' | 'COCKTAIL' | 'FOOD' | 'MIXED' | 'OTHER';

interface UseCaseDef {
  key: UseCase;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const USE_CASES: UseCaseDef[] = [
  { key: 'WINE_LIST', title: 'Wine List',     description: 'For bottle lists, by-the-glass lists, reserve lists and event wine lists.',  icon: <WineListIcon /> },
  { key: 'COCKTAIL',  title: 'Cocktail Menu', description: 'For house cocktails, classics, seasonal drinks and bar menus.',              icon: <CocktailIcon /> },
  { key: 'FOOD',      title: 'Food Menu',     description: 'For dishes, snacks, tasting menus and food sections.',                       icon: <FoodIcon /> },
  { key: 'MIXED',     title: 'Mixed Menu',    description: 'For menus combining wine, cocktails and other item types.',                  icon: <MixedIcon /> },
  { key: 'OTHER',     title: 'Other',         description: 'For special cases or future menu structures.',                                icon: <OtherIcon /> },
];

const labelForUseCase = (uc: UseCase): string =>
  USE_CASES.find(u => u.key === uc)?.title ?? uc;

/** Wine-list subtypes — the 5 MenuType values that map to the "Wine List" use case. */
const WINE_LIST_SUBTYPES: MenuType[] = ['FULL', 'BY_THE_GLASS', 'RESERVE', 'EVENT', 'PRIVATE'];

/**
 * Types shown when the operator picks "Other" — everything that isn't already
 * covered by a dedicated card. Keeps the rare-cases path narrow.
 */
const OTHER_TYPES: MenuType[] = ['BEER', 'SPIRITS', 'TASTING', 'RETAIL', 'OTHER'];

/** All types — kept for edit-mode dropdown so any existing menu's type is selectable. */
const ALL_MENU_TYPES: MenuType[] = [
  'FULL', 'BY_THE_GLASS', 'RESERVE', 'EVENT', 'PRIVATE',
  'COCKTAIL', 'FOOD', 'BEER', 'SPIRITS', 'TASTING', 'RETAIL', 'MIXED', 'OTHER',
];

const MENU_STATUSES: MenuStatus[] = ['DRAFT', 'ACTIVE', 'ARCHIVED'];

interface FormData {
  name: string;
  type: MenuType;
  status: MenuStatus;
  description: string;
}

const INITIAL_FORM: FormData = {
  name: '',
  // Pre-create the form with a benign placeholder type; the card picker
  // overrides this as soon as the operator chooses a use case, and validation
  // refuses to submit until they've done so.
  type: 'COCKTAIL',
  status: 'DRAFT',
  description: '',
};

const MenuForm: React.FC = () => {
  const navigate = useNavigate();
  const { menuId } = useParams<{ menuId?: string }>();
  const theme = useTheme();
  const { venueId } = useVenue();
  const isEdit = Boolean(menuId);
  const backPath = `/admin/venues/${venueId}/menus`;

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData | 'useCase', string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  // Use-case selection (create mode only). `null` = picker not yet completed.
  // In edit mode this state is unused — the simple dropdown is rendered instead.
  const [useCase, setUseCase] = useState<UseCase | null>(null);

  useEffect(() => {
    if (!isEdit || !menuId) return;
    setLoading(true);
    menuService.getMenu(venueId, menuId)
      .then(menu => {
        setFormData({
          name: menu.name,
          type: menu.type,
          status: menu.status,
          description: menu.description ?? '',
        });
      })
      .catch(() => setApiError('Failed to load menu.'))
      .finally(() => setLoading(false));
  }, [isEdit, menuId, venueId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  // ── Use-case → MenuType mapping ──────────────────────────────────────────
  const handlePickUseCase = (uc: UseCase) => {
    setUseCase(uc);
    setErrors(prev => ({ ...prev, useCase: undefined, type: undefined }));
    // Each use case nails the underlying MenuType. Wine List defaults to FULL
    // and exposes a subtype select. Other reveals the full dropdown but seeds
    // a sensible default.
    let nextType: MenuType;
    switch (uc) {
      case 'WINE_LIST': nextType = 'FULL'; break;
      case 'COCKTAIL':  nextType = 'COCKTAIL'; break;
      case 'FOOD':      nextType = 'FOOD'; break;
      case 'MIXED':     nextType = 'MIXED'; break;
      case 'OTHER':     nextType = 'OTHER'; break;
    }
    setFormData(prev => ({ ...prev, type: nextType }));
  };

  const handleResetUseCase = () => {
    setUseCase(null);
    // Reset type back to the neutral default so a half-typed name doesn't
    // accidentally save with the prior use-case's type.
    setFormData(prev => ({ ...prev, type: INITIAL_FORM.type }));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData | 'useCase', string>> = {};

    // Create-mode-only: require a use case before anything else.
    if (!isEdit && !useCase) {
      newErrors.useCase = 'Pick what kind of menu you are creating.';
    }
    if (!formData.name.trim()) newErrors.name = 'Menu name is required.';
    if (!formData.type) newErrors.type = 'Type is required.';
    if (!formData.status) newErrors.status = 'Status is required.';

    // Wine List use-case: type must be one of the 5 declared subtypes.
    if (!isEdit && useCase === 'WINE_LIST' && !WINE_LIST_SUBTYPES.includes(formData.type)) {
      newErrors.type = 'Pick a wine list subtype.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setApiError('');
    try {
      // Backend still receives the same MenuType enum value; the use-case
      // concept is purely a UI-side scaffold and is never sent.
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        status: formData.status,
        description: formData.description.trim() || undefined,
      };
      if (isEdit && menuId) {
        await menuService.updateMenu(venueId, menuId, payload);
        navigate(`${backPath}/${menuId}`);
      } else {
        const created = await menuService.createMenu(
          venueId,
          payload as Parameters<typeof menuService.createMenu>[1],
        );
        navigate(`${backPath}/${created._id}`);
      }
    } catch (err: any) {
      console.error('Failed to save menu', err);
      const code = err?.response?.data?.code;
      if (code === 'MENU_NAME_DUPLICATE') {
        setErrors(prev => ({ ...prev, name: 'A menu with this name already exists in this venue.' }));
      } else {
        setApiError(err?.response?.data?.message ?? 'Failed to save menu. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Convenience: in create mode the rest of the form (name, status, description,
  // submit) only renders once the operator has picked a use case. In edit mode
  // there is no use-case picker, so it's always rendered.
  const showFormFields = isEdit || !!useCase;

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Menus
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit Menu' : 'New Menu'}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          <Alert severity="info" sx={{ borderRadius: 1 }}>
            Menus are the shared engine for wine lists, cocktail menus and future food or mixed menus.
          </Alert>

          {/* ── Create mode: card picker step ───────────────────────────── */}
          {!isEdit && !useCase && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                What kind of menu are you creating?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Pick a use case to continue. You can change the type later from the menu's edit page.
              </Typography>
              {errors.useCase && (
                <Alert severity="error" sx={{ borderRadius: 1, mb: 2 }}>{errors.useCase}</Alert>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.5,
                }}
              >
                {USE_CASES.map(uc => (
                  <Paper
                    key={uc.key}
                    variant="outlined"
                    onClick={() => handlePickUseCase(uc.key)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: 'pointer',
                      borderColor: alpha(theme.palette.divider, 0.5),
                      transition: 'all 0.15s ease',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <Box sx={{ color: theme.palette.primary.main, mt: 0.25, '& svg': { fontSize: '1.5rem' } }}>
                      {uc.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={600}>{uc.title}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
                        {uc.description}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Create mode: chosen use-case summary + per-use-case follow-up ── */}
          {!isEdit && useCase && (
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <CheckIcon sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{labelForUseCase(useCase)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {USE_CASES.find(u => u.key === useCase)?.description}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={handleResetUseCase}
                  sx={{ textTransform: 'none' }}
                >
                  Change menu type
                </Button>
              </Paper>

              {/* WINE_LIST → subtype dropdown */}
              {useCase === 'WINE_LIST' && (
                <FormControl fullWidth required error={Boolean(errors.type)} sx={{ mt: 2 }}>
                  <InputLabel>Wine list subtype</InputLabel>
                  <Select
                    value={formData.type}
                    label="Wine list subtype"
                    onChange={e => {
                      setFormData(prev => ({ ...prev, type: e.target.value as MenuType }));
                      setErrors(prev => ({ ...prev, type: undefined }));
                    }}
                  >
                    {WINE_LIST_SUBTYPES.map(t => (
                      <MenuItem key={t} value={t}>{menuTypeLabel(t)}</MenuItem>
                    ))}
                  </Select>
                  {errors.type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.type}</Typography>
                  )}
                </FormControl>
              )}

              {/* OTHER → full dropdown of remaining types */}
              {useCase === 'OTHER' && (
                <FormControl fullWidth required error={Boolean(errors.type)} sx={{ mt: 2 }}>
                  <InputLabel>Menu type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Menu type"
                    onChange={e => {
                      setFormData(prev => ({ ...prev, type: e.target.value as MenuType }));
                      setErrors(prev => ({ ...prev, type: undefined }));
                    }}
                  >
                    {OTHER_TYPES.map(t => (
                      <MenuItem key={t} value={t}>{menuTypeLabel(t)}</MenuItem>
                    ))}
                  </Select>
                  {errors.type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.type}</Typography>
                  )}
                </FormControl>
              )}

              {/* FOOD → helper copy (dish authoring is live as of Phase 7.C.2). */}
              {useCase === 'FOOD' && (
                <Alert severity="info" sx={{ borderRadius: 1, mt: 2 }}>
                  Food menus support dish authoring with serving options and optional Stock &amp; recipe setup.
                </Alert>
              )}

              {/* Phase 7.C.3b — TASTING is still gated: the container can be
                  created, but item authoring is coming soon (may need course /
                  pairing structure). */}
              {useCase === 'OTHER' && formData.type === 'TASTING' && (
                <Alert severity="info" sx={{ borderRadius: 1, mt: 2 }}>
                  Container only for now — tasting menu item authoring is coming soon. This menu type may need course and pairing structure, so it is not enabled yet.
                </Alert>
              )}
            </Box>
          )}

          {/* ── Form fields (name, status, description). In create mode these
                only render once a use case is picked. ───────────────────── */}
          {showFormFields && (
            <>
              <TextField
                label="Menu Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={Boolean(errors.name)}
                helperText={errors.name}
                fullWidth
                required
                placeholder="e.g. Summer Cocktails"
              />

              {/* Edit mode: keep the simple Type select; create mode: type is
                  determined by use-case + subtype (handled above) so no extra
                  control is needed here. */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {isEdit && (
                  <FormControl fullWidth required error={Boolean(errors.type)}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Type"
                      onChange={e => {
                        setFormData(prev => ({ ...prev, type: e.target.value as MenuType }));
                        setErrors(prev => ({ ...prev, type: undefined }));
                      }}
                    >
                      {ALL_MENU_TYPES.map(t => (
                        <MenuItem key={t} value={t}>{menuTypeLabel(t)}</MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color={errors.type ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.type ?? 'Changing menu type affects default item-creation behavior, but does not convert existing items.'}
                    </Typography>
                  </FormControl>
                )}

                <FormControl fullWidth required error={Boolean(errors.status)}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={e => {
                      setFormData(prev => ({ ...prev, status: e.target.value as MenuStatus }));
                      setErrors(prev => ({ ...prev, status: undefined }));
                    }}
                  >
                    {MENU_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                  {errors.status && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.status}</Typography>
                  )}
                </FormControl>
              </Box>

              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Optional internal description"
              />
            </>
          )}

          {/* Actions — always shown; submit is disabled implicitly via
              validateForm when the create-mode use-case isn't picked. */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || (!isEdit && !useCase)}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Create Menu'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MenuForm;
